import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { catchError, map, tap, mergeMap } from 'rxjs/operators';
import { of as RxOf } from 'rxjs/observable/of';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { cloneDeep as _cloneDeep } from 'lodash';

import { IAccountGenericSetting } from '@blcloud/bl-datamodel';
import { AccountGenericSettingResource } from '@blcloud/bl-ng-resource';
import { BlApiErrorInfo, HttpStatus } from '@blcloud/bl-ng-common';
import { PageId, LoginInfoResourceService } from '@blcloud/bl-ng-share-module';

import {
  SCREEN_ACTION_SETTING_ID,
  ISalesSlipSearchScreenActionSetting,
  BaseScreenActionSetting,
} from './sales-slip-search-screen-action-setting-modal.define';

/**
 * 伝票検索画面動作設定管理サービス
 */
@Injectable()
export class SalesSlipSearchScreenActionSettingService {
  /** アカウント別汎用設定 */
  private _accountGenericSetting: IAccountGenericSetting;

  /** 伝票検索画面動作設定 */
  private _screenActionSetting: ISalesSlipSearchScreenActionSetting;
  /** 伝票検索画面動作設定 */
  get screenActionSetting(): ISalesSlipSearchScreenActionSetting {
    return this._screenActionSetting;
  }

  /** 伝票検索画面動作設定変更通知Subject */
  private changeSettingSubject = new BehaviorSubject<ISalesSlipSearchScreenActionSetting>(undefined);
  /** 伝票検索画面動作設定変更通知ストリーム */
  get changeSetting$(): Observable<ISalesSlipSearchScreenActionSetting> {
    return this.changeSettingSubject.asObservable();
  }

  constructor(
    private loginInfoResourceService: LoginInfoResourceService,
    private accountGenericSettingResource: AccountGenericSettingResource,
  ) {
  }

  /**
   * 伝票検索画面動作設定を取得します。
   * @returns 伝票検索画面動作設定
   */
  fetch(): Observable<ISalesSlipSearchScreenActionSetting> {
    return this.getAccountGenericSetting().pipe(
      tap(accountGenericSetting => {
        this._accountGenericSetting = accountGenericSetting;
      }),
      map(accountGenericSetting => {
        return this.parseScreenActionSetting(accountGenericSetting);
      }),
      tap(screenActionSetting => {
        this._screenActionSetting = screenActionSetting;
        this.changeSettingSubject.next(screenActionSetting);
      }),
    );
  }

  /**
   * 伝票検索画面動作設定を保存します。
   * @param screenActionSetting 伝票検索画面動作設定
   */
  save(screenActionSetting: ISalesSlipSearchScreenActionSetting): Observable<any> {
    if (this._accountGenericSetting) {
      // 更新
      const entity: IAccountGenericSetting = {
        blTenantId: this._accountGenericSetting.blTenantId,
        blUserId: this._accountGenericSetting.blUserId,
        pageId: this._accountGenericSetting.pageId,
        settingDataId: this._accountGenericSetting.settingDataId,
        settingInfoDataJson: JSON.stringify(screenActionSetting)
      };

      return this.putAccountGenericSetting(entity).pipe(
        tap(() => {
          this._accountGenericSetting = entity;
          this._screenActionSetting = screenActionSetting;
          this.changeSettingSubject.next(screenActionSetting);
        })
      );

    } else {
      // 新規作成
      const entity: IAccountGenericSetting = {
        pageId: PageId.SfSalesSlipSearch,
        settingDataId: SCREEN_ACTION_SETTING_ID,
        settingInfoDataJson: JSON.stringify(screenActionSetting)
      };

      return this.postAccountGenericSetting(entity).pipe(
        tap(() => {
          this._accountGenericSetting = entity;
          this._screenActionSetting = screenActionSetting;
          this.changeSettingSubject.next(screenActionSetting);
        })
      );
    }
  }

  /**
   * blUserId取得
   */
  private getBlUserId(): Observable<string> {
    return this.loginInfoResourceService.getLoginInfo().pipe(
      map(loginInfo => loginInfo.blUserId)
    );
  }

  /**
   * アカウント別汎用設定を取得します
   * @returns アカウント別汎用設定
   */
  private getAccountGenericSetting(): Observable<IAccountGenericSetting> {
    const pageId = PageId.SfSalesSlipSearch;
    const settingDataId = SCREEN_ACTION_SETTING_ID;

    return this.getBlUserId().pipe(
      mergeMap(blUserId => {
        const id = this.accountGenericSettingResource.getIdString({
          blUserId,
          pageId,
          settingDataId
        });
        return this.accountGenericSettingResource.getById(id);
      }),
      catchError((error: BlApiErrorInfo) => {
        if (error.status === HttpStatus.NotFound) {
          return RxOf(null);
        } else {
          throw error;
        }
      })
    );
  }

  /**
   * アカウント別汎用設定を更新します。
   * @param setting アカウント別汎用設定
   */
  private putAccountGenericSetting(setting: IAccountGenericSetting): Observable<any> {
    return this.accountGenericSettingResource.putByEntity(setting);
  }

  /**
   * アカウント別汎用設定を登録します。
   * @param setting アカウント別汎用設定
   */
  private postAccountGenericSetting(setting: IAccountGenericSetting): Observable<any> {
    return this.getBlUserId().pipe(
      mergeMap(blUserId => {
        setting.blUserId = blUserId;
        return this.accountGenericSettingResource.post(setting);
      })
    );
  }

  /**
   * 伝票検索画面動作設定にparseします。
   * @param accountGenericSetting アカウント別汎用設定
   * @returns 伝票検索画面動作設定
   */
  private parseScreenActionSetting(accountGenericSetting?: IAccountGenericSetting): ISalesSlipSearchScreenActionSetting {
    const baseSetting = _cloneDeep(BaseScreenActionSetting);
    if (accountGenericSetting) {
      // parseできないときはベースを採用
      try {
        return {
          ...baseSetting,
          ...<ISalesSlipSearchScreenActionSetting>JSON.parse(accountGenericSetting.settingInfoDataJson),
        };
      } catch {
        return baseSetting;
      }
    }

    return baseSetting;
  }
}



// WEBPACK FOOTER //
// ./src/app/feature/sales-slip-search-screen-action-setting-modal/sales-slip-search-screen-action-setting-modal.service.ts